import { Add } from '@material-ui/icons';
import { palette as jigsawPalette, BaseClickable, Stack, Text } from '@solvhealth/jigsaw-v1';
import React, { forwardRef, Ref } from 'react';
import Dropzone, { DropzoneRef } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { usePalette } from '~/hooks/usePalette';
import { Side } from '../util/usePhotoIdUpload';

type Props = {
  side: Side;
  imageData?: string;
  handleUpload: (a: any) => any;
  hidden?: boolean;
  onDropSideEffect?: (val: boolean) => any;
};

const DropzoneUploader = (
  { side, imageData, handleUpload, hidden, onDropSideEffect }: Props,
  ref: Ref<DropzoneRef>
) => {
  const onDrop = (files: File[]) => {
    if (onDropSideEffect) {
      onDropSideEffect(true);
    }
    handleUpload(files[0]);
  };

  const { t } = useTranslation(['paperwork']);

  const { palette } = usePalette();

  return (
    <Dropzone accept="image/*" maxFiles={1} onDrop={onDrop} ref={ref}>
      {({ getRootProps, getInputProps, isDragActive }) => (
        <BaseClickable
          alignItems="center"
          backgroundPosition="center"
          backgroundSize="cover"
          bg={isDragActive ? 'crunchberry-light' : 'crunchberry-xlight'}
          border={imageData ? 'none' : 'dashed 1px'}
          borderColor="crunchberry"
          borderRadius="xl"
          boxShadow={imageData ? 'md' : 'none'}
          contentProps={{
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-hj-suppress
          display={hidden ? 'none' : 'flex'}
          height="196px"
          justifyContent="center"
          overflow="hidden"
          sx={{
            transition: 'all 75ms',
            //@ts-ignore
            ':hover': {
              bg: palette['200'] ?? 'crunchberry-light',
            },
            backgroundImage: imageData ? `url(${imageData})` : undefined,
          }}
          width="331px"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {!imageData && (
            <Stack alignItems="center" style={{ pointerEvents: 'none' }}>
              <Add htmlColor={palette.base} />
              <Text textColor="crunchberry" variant="subtitle">
                {t(
                  side === 'front'
                    ? 'paperwork:photoId.ctas.addFrontPhoto'
                    : 'paperwork:photoId.ctas.addBackPhoto'
                )}
              </Text>
              <Text>{t('paperwork:photoId.dragAndDrop')}</Text>
            </Stack>
          )}
          {imageData && isDragActive && (
            <Stack
              alignItems="center"
              alignSelf="stretch"
              flex={1}
              height="100%"
              justifyContent="center"
              justifySelf="stretch"
              style={{ pointerEvents: 'none' }}
              sx={{ background: 'rgba(0, 0, 0, 0.5)' }}
              width="100%"
            >
              <Add htmlColor={jigsawPalette.egg} />
              <Text textColor="white" variant="subtitle">
                {t(
                  side === 'front'
                    ? 'paperwork:photoId.ctas.addFrontPhoto'
                    : 'paperwork:photoId.ctas.addBackPhoto'
                )}
              </Text>
              <Text textColor="white">{t('paperwork:photoId.dragAndDrop')}</Text>
            </Stack>
          )}
        </BaseClickable>
      )}
    </Dropzone>
  );
};

export default forwardRef(DropzoneUploader);
